import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AnnotationLayer } from "../components/AnnotationLayer.js";
import { IIIFViewer } from "../components/IIIFViewer.js";
import { PageNav } from "../components/PageNav.js";
import {
  deleteAnnotation,
  loadDiary,
  loadPage,
  transcriptionAnnotations,
  translationAnnotations,
  translationFor,
  updatePageMetadata,
  updateTranscription,
  updateTranslation,
} from "../data/loader.js";
import type { DiaryWithPages, Language, PageData } from "../data/types.js";
import { LANG_LABEL } from "../lib/i18n.js";
import { t } from "../lib/strings.js";

type EditTarget = "transcription" | `translation:${Language}`;

interface Props {
  lang: Language;
}

const LANG_OPTS: Language[] = ["de", "en", "eo", "fr"];

export function AdminPageEditor({ lang }: Props): JSX.Element {
  const s = t(lang);
  const params = useParams<{ slug: string; pageNumber?: string }>();
  const slug = params.slug ?? "";
  const navigate = useNavigate();

  const [diary, setDiary] = useState<DiaryWithPages | null>(null);
  const [page, setPage] = useState<PageData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [target, setTarget] = useState<EditTarget>("transcription");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [authorLabelDraft, setAuthorLabelDraft] = useState("");
  const [authorLabelSaving, setAuthorLabelSaving] = useState(false);

  const pageNumber = (() => {
    const parsed = Number.parseInt(params.pageNumber ?? "1", 10);
    return Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
  })();

  useEffect(() => {
    setError(null);
    loadDiary(slug)
      .then(setDiary)
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, [slug]);

  useEffect(() => {
    if (!diary) return;
    const clamped = Math.min(Math.max(1, pageNumber), diary.pages.length);
    if (clamped !== pageNumber) {
      navigate(`/admin/diary/${slug}/page/${clamped}`, { replace: true });
      return;
    }
    setPage(null);
    setEditing(false);
    loadPage(slug, clamped)
      .then((p) => {
        setPage(p);
        setAuthorLabelDraft(p.page.authorLabel ?? "");
      })
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, [diary, slug, pageNumber, navigate]);

  const txAnns = page ? transcriptionAnnotations(page) : [];
  const targetText = (() => {
    if (!page) return "";
    if (target === "transcription") return page.transcription?.text ?? "";
    const lng = target.slice("translation:".length) as Language;
    return translationFor(page, lng)?.text ?? "";
  })();
  const targetAnns = (() => {
    if (!page) return [];
    if (target === "transcription") return txAnns;
    const lng = target.slice("translation:".length) as Language;
    return translationAnnotations(page, lng);
  })();

  function startEdit(): void {
    setDraft(targetText);
    setEditing(true);
  }
  function cancelEdit(): void {
    setEditing(false);
    setDraft("");
  }

  async function saveEdit(): Promise<void> {
    if (!page) return;
    setSaving(true);
    try {
      if (target === "transcription") {
        await updateTranscription(page.page.id, draft);
      } else {
        const lng = target.slice("translation:".length) as Language;
        await updateTranslation(page.page.id, lng, draft);
      }
      const fresh = await loadPage(slug, pageNumber);
      setPage(fresh);
      setAuthorLabelDraft(fresh.page.authorLabel ?? "");
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteAnnotation(annotationId: string): Promise<void> {
    if (!page) return;
    if (!window.confirm("Delete this annotation?")) return;
    try {
      await deleteAnnotation(page.page.id, annotationId);
      const fresh = await loadPage(slug, pageNumber);
      setPage(fresh);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function saveAuthorLabel(): Promise<void> {
    if (!page) return;
    const v = authorLabelDraft.trim();
    if (v === (page.page.authorLabel ?? "")) return;
    setAuthorLabelSaving(true);
    try {
      await updatePageMetadata(page.page.id, { authorLabel: v === "" ? null : v });
      const fresh = await loadPage(slug, pageNumber);
      setPage(fresh);
      setAuthorLabelDraft(fresh.page.authorLabel ?? "");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setAuthorLabelSaving(false);
    }
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8 text-red-700">
        {s.errorPrefix}: {error}
      </div>
    );
  }
  if (!diary) {
    return <div className="max-w-6xl mx-auto px-4 py-8 text-ink-muted">{s.loadingDiary(0)}</div>;
  }

  const pageRef = diary.pages[pageNumber - 1];
  const jump = (n: number) => navigate(`/admin/diary/${slug}/page/${n}`);

  return (
    <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-3 lg:min-h-[calc(100vh-7rem)]">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <h1 className="font-serif text-xl">
          <span className="text-ink-muted text-sm uppercase tracking-wider mr-2">Admin</span>
          Journal No. {diary.diary.number}
          {diary.diary.dateLabel && (
            <span className="text-sm text-ink-muted ml-2">({diary.diary.dateLabel})</span>
          )}
        </h1>
        <div className="flex items-center gap-3 text-xs">
          <label className="flex items-center gap-1.5 text-ink-muted">
            Author page #
            <input
              type="text"
              value={authorLabelDraft}
              onChange={(e) => setAuthorLabelDraft(e.target.value)}
              onBlur={saveAuthorLabel}
              onKeyDown={(e) => {
                if (e.key === "Enter") (e.currentTarget as HTMLInputElement).blur();
              }}
              disabled={authorLabelSaving || !page}
              className="w-20 px-1.5 py-0.5 border border-parchment-300 rounded font-serif text-ink"
              placeholder="—"
            />
          </label>
          <a
            href={`/diary/${slug}/page/${pageNumber}`}
            className="text-ink-muted hover:text-ink"
          >
            ← Reader view
          </a>
        </div>
      </div>

      <PageNav
        slug={slug}
        pageNumber={pageNumber}
        pageCount={diary.pages.length}
        onJump={jump}
        lang={lang}
      />

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-3 lg:min-h-0">
        <div className="h-[60vh] lg:h-auto lg:min-h-0">
          {pageRef && (
            <IIIFViewer
              diaryId={diary.diary.onbId}
              imageId={pageRef.imageId}
              width={pageRef.width}
              height={pageRef.height}
            />
          )}
        </div>
        <div className="flex flex-col lg:min-h-0 bg-white border border-parchment-200 rounded-md overflow-hidden">
          <div className="flex items-center border-b border-parchment-200 text-sm">
            <button
              type="button"
              className={`px-3 py-2 ${
                target === "transcription"
                  ? "bg-parchment-100 font-semibold"
                  : "text-ink-muted hover:bg-parchment-50"
              }`}
              onClick={() => {
                setTarget("transcription");
                setEditing(false);
              }}
            >
              Transcription
            </button>
            {LANG_OPTS.map((l) => (
              <button
                key={l}
                type="button"
                className={`px-3 py-2 border-l border-parchment-200 ${
                  target === `translation:${l}`
                    ? "bg-parchment-100 font-semibold"
                    : "text-ink-muted hover:bg-parchment-50"
                }`}
                onClick={() => {
                  setTarget(`translation:${l}`);
                  setEditing(false);
                }}
              >
                {LANG_LABEL[l]}
              </button>
            ))}
          </div>

          {editing ? (
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              spellCheck={false}
              className="flex-1 lg:overflow-y-auto p-4 font-serif text-base leading-relaxed resize-none focus:outline-none"
            />
          ) : (
            <div className="flex-1 lg:overflow-y-auto p-4 prose-diary">
              {targetText ? (
                <AnnotationLayer text={targetText} annotations={targetAnns} lang={lang} />
              ) : (
                <div className="text-ink-muted text-sm italic">
                  {target === "transcription" ? s.noTranscription : s.noTranslation}
                </div>
              )}
            </div>
          )}

          <div className="border-t border-parchment-200 px-4 py-2 text-xs bg-parchment-50 flex items-center justify-between gap-2">
            {editing ? (
              <>
                <span className="text-ink-muted italic">
                  Editing — changes will create a new revision.
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={cancelEdit}
                    disabled={saving}
                    className="px-3 py-1 rounded text-xs hover:bg-parchment-100"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={saveEdit}
                    disabled={saving}
                    className="px-3 py-1 rounded bg-ink text-parchment-100 hover:bg-ink-muted text-xs disabled:opacity-60"
                  >
                    {saving ? "Saving…" : "Save"}
                  </button>
                </div>
              </>
            ) : (
              <>
                <span className="text-ink-muted">
                  {targetText.length > 0
                    ? `${targetText.length} chars, ${(targetText.match(/\n/g)?.length ?? 0) + 1} lines`
                    : "empty"}
                </span>
                <button
                  type="button"
                  onClick={startEdit}
                  disabled={!page}
                  className="px-3 py-1 rounded bg-ink text-parchment-100 hover:bg-ink-muted text-xs"
                >
                  Edit
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {targetAnns.length > 0 && (
        <div className="bg-white border border-parchment-200 rounded-md p-3 text-sm">
          <div className="text-xs text-ink-muted uppercase tracking-wider mb-2">
            Annotations on this target ({targetAnns.length})
          </div>
          <ul className="divide-y divide-parchment-200">
            {targetAnns
              .slice()
              .sort((a, b) => a.startOffset - b.startOffset)
              .map((a) => {
                const sliced = targetText.slice(a.startOffset, a.endOffset);
                return (
                  <li key={a.id} className="py-1.5 flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <span className="text-xs text-ink-muted uppercase tracking-wider mr-2">
                        {a.type}
                      </span>
                      <span className="font-serif">
                        @{a.startOffset}..{a.endOffset}: «{sliced || "∅"}»
                      </span>
                      {a.note && (
                        <span className="text-ink-muted text-xs ml-2">— {a.note}</span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDeleteAnnotation(a.id)}
                      className="text-xs text-red-700 hover:text-red-900 px-2 py-1 rounded hover:bg-red-50"
                    >
                      Delete
                    </button>
                  </li>
                );
              })}
          </ul>
        </div>
      )}
    </div>
  );
}
